/**
 * News/newsAPI.js
 * ════════════════════════════════════════════════════════════════
 * Спільний API-шар для новин. Один набір функцій обслуговує
 * обидва рівні — передається scope ('portal' | 'kg') і kgId
 * (тільки для scope='kg').
 *
 * ВИКОРИСТАННЯ:
 *   newsAPI.getAdminList('portal')
 *   newsAPI.getAdminList('kg', kgId)
 * ════════════════════════════════════════════════════════════════
 */

import apiClient, { apiSendFileHeader } from '../../../../../services/apiClient';

// Скоригуй шлях залежно від структури проекту

function basePath(scope, kgId) {
    return scope === 'kg' ? `news/kg/${kgId}` : 'news/portal';
}

function readFileAsBuffer(file) {
    return new Promise(function(resolve, reject) {
        var reader = new FileReader();
        reader.onload  = function() { resolve(reader.result); };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    });
}

// Конвертує base64 data URL (з FileUploader, asBase64=true) назад у File —
// для передачі в наш бінарний upload (apiSendFileHeader)
export function base64ToFile(dataUrl, filename, mtype) {
    var arr = dataUrl.split(',');
    var mimeMatch = arr[0].match(/:(.*?);/);
    var mime = mtype || (mimeMatch ? mimeMatch[1] : 'application/octet-stream');
    var bstr = atob(arr[1]);
    var n = bstr.length;
    var u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
}

export const newsAPI = {

    // ── Адмін — список і CRUD ────────────────────────────────────

    async getAdminList(scope, kgId, filters) {
        var f = filters || {};
        var qs = [];
        if (f.status)   qs.push('status='   + encodeURIComponent(f.status));
        if (f.category) qs.push('category=' + encodeURIComponent(f.category));
        if (f.search)   qs.push('search='   + encodeURIComponent(f.search));
        if (f.page)     qs.push('page='     + f.page);
        if (f.limit)    qs.push('limit='    + f.limit);
        var url = basePath(scope, kgId) + (qs.length ? '?' + qs.join('&') : '');

        const response = await apiClient.get(url);
        return response.data || { items: [], total: 0, page: 1, pages: 0 };
    },

    async getById(scope, kgId, id) {
        const response = await apiClient.get(`${basePath(scope, kgId)}/${id}`);
        return response.data;
    },

    async create(scope, kgId, payload) {
        const response = await apiClient.post(basePath(scope, kgId), payload);
        return response.data;
    },

    async update(scope, kgId, id, payload) {
        const response = await apiClient.put(`${basePath(scope, kgId)}/${id}`, payload);
        return response.data;
    },

    async remove(scope, kgId, id) {
        const response = await apiClient.delete(`${basePath(scope, kgId)}/${id}`);
        return response.data;
    },

    async changeStatus(scope, kgId, id, status) {
        const response = await apiClient.patch(`${basePath(scope, kgId)}/${id}/status`, { status });
        return response.data;
    },

    // ── Медіа — обкладинка ────────────────────────────────────────

    async uploadCover(scope, kgId, id, file) {
        const fileInfo = {
            news_id: id,
            name:    file.name,
            mtype:   file.type,
            length:  file.size,
            date:    new Date().toISOString(),
        };
        const buffer = await readFileAsBuffer(file);
        const response = await apiSendFileHeader(
            `${basePath(scope, kgId)}/${id}/cover`,
            buffer,
            fileInfo
        );
        if (!response.ok) throw new Error('Upload failed: ' + response.status);
        const json = await response.json();
        return json.data || json;
    },

    async deleteCover(scope, kgId, id) {
        const response = await apiClient.delete(`${basePath(scope, kgId)}/${id}/cover`);
        return response.data;
    },

    // ── Галерея (фото + YouTube) ────────────────────────────────────

    async getGallery(scope, kgId, id) {
        const response = await apiClient.get(`${basePath(scope, kgId)}/${id}/gallery`);
        return response.data || { docId: null, files: [] };
    },

    async uploadGalleryPhoto(scope, kgId, id, file) {
        const fileInfo = {
            news_id: id,
            name:    file.name,
            mtype:   file.type,
            length:  file.size,
            date:    new Date().toISOString(),
        };
        const buffer = await readFileAsBuffer(file);
        const response = await apiSendFileHeader(
            `${basePath(scope, kgId)}/${id}/gallery/photo`,
            buffer,
            fileInfo
        );
        if (!response.ok) throw new Error('Upload failed: ' + response.status);
        const json = await response.json();
        return json.data || json;
    },

    async addGalleryVideo(scope, kgId, id, url, caption) {
        const response = await apiClient.post(`${basePath(scope, kgId)}/${id}/gallery/video`, { url, caption: caption || '' });
        return response.data;
    },

    async updateGalleryCaption(scope, kgId, id, fileId, caption) {
        const response = await apiClient.patch(`${basePath(scope, kgId)}/${id}/gallery/${fileId}/caption`, { caption });
        return response.data;
    },

    async deleteGalleryFile(scope, kgId, id, fileId) {
        const response = await apiClient.delete(`${basePath(scope, kgId)}/${id}/gallery/${fileId}`);
        return response.data;
    },

    // ── Публічні ──────────────────────────────────────────────────

    async getPublicList(scope, kgId, filters) {
        var f = filters || {};
        var qs = [];
        if (f.category) qs.push('category=' + encodeURIComponent(f.category));
        if (f.tag)       qs.push('tag='      + encodeURIComponent(f.tag));
        if (f.page)      qs.push('page='     + f.page);
        if (f.limit)     qs.push('limit='    + f.limit);
        var base = scope === 'kg' ? `public/news/kg/${kgId}` : 'public/news/portal';
        var url  = base + (qs.length ? '?' + qs.join('&') : '');

        const response = await apiClient.get(url);
        return response.data || { items: [], total: 0, page: 1, pages: 0, hasMore: false };
    },

    async getPublicById(scope, kgId, id) {
        var base = scope === 'kg' ? `public/news/kg/${kgId}` : 'public/news/portal';
        const response = await apiClient.get(`${base}/${id}`);
        return response.data;
    },

    async getPublicGallery(scope, kgId, id) {
        var base = scope === 'kg' ? `public/news/kg/${kgId}` : 'public/news/portal';
        const response = await apiClient.get(`${base}/${id}/gallery`);
        return response.data || { docId: null, files: [] };
    },
};

// ── Категорії (спільний довідник, керує admin) ─────────────────────

export const newsCategoriesAPI = {
    async list(status) {
        var url = 'news-categories/list' + (status ? '?status=' + status : '');
        const response = await apiClient.get(url);
        var list = response.data;
        return Array.isArray(list) ? list : [];
    },

    async create(payload) {
        const response = await apiClient.post('news-categories', payload);
        return response.data;
    },

    async update(id, payload) {
        const response = await apiClient.put(`news-categories/${id}`, payload);
        return response.data;
    },

    async changeStatus(id, status) {
        const response = await apiClient.patch(`news-categories/${id}/status`, { status });
        return response.data;
    },
};